import { useState, useEffect, useCallback } from 'react';
import { fetchAvailableSlots, fetchBarberSchedule, createAppointment } from '../data/api';

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const DAYS_SHORT = ['PZT','SAL','ÇAR','PER','CUM','CMT','PAZ'];

export default function BookingModal({ barberId, services, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [slots, setSlots] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBarberSchedule(barberId).then(setSchedule).catch(() => {});
  }, [barberId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const goTo = useCallback((n) => setStep(n), []);

  // Toggle service selection
  const toggleService = (svc) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svc.id);
      if (exists) return prev.filter(s => s.id !== svc.id);
      return [...prev, svc];
    });
  };

  const totalPrice = selectedServices.reduce((sum, s) => sum + parseFloat(s.price), 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);

  const goToCalendar = () => {
    if (selectedServices.length === 0) return;
    goTo(2);
  };

  const isDayOff = (weekday) => {
    const day = schedule[weekday];
    return !day || day.is_off;
  };

  const pickDate = async (dateStr) => {
    setDate(dateStr);
    setLoading(true);
    try {
      const data = await fetchAvailableSlots(barberId, dateStr);
      setSlots(data.slots || []);
    } catch {
      setSlots([]);
    }
    setLoading(false);
    goTo(3);
  };

  const pickTime = (t) => {
    setTime(t);
    setTimeout(() => goTo(4), 250);
  };

  const submit = async () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Lütfen adınızı giriniz';
    }
    const cleanPhone = phone.trim().replace(/\s+/g, '');
    if (!cleanPhone) {
      newErrors.phone = 'Lütfen telefon numaranızı giriniz';
    } else if (!/^0\d{10}$/.test(cleanPhone)) {
      newErrors.phone = 'Geçerli bir telefon numarası giriniz (05XX XXX XX XX)';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.name) document.querySelector('#custName')?.focus();
      else if (newErrors.phone) document.querySelector('#custPhone')?.focus();
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      await createAppointment({
        barber: barberId,
        services: selectedServices.map(s => s.id),
        date,
        time: time + ':00',
        customer_name: name.trim(),
        customer_phone: cleanPhone,
      });
      goTo(5);
    } catch (err) {
      console.error('Randevu hatası:', err);
      alert('Randevu oluşturulamadı. Lütfen tekrar deneyiniz.');
    }
    setLoading(false);
  };

  const changeMonth = (dir) => {
    let m = calMonth + dir;
    let y = calYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setCalMonth(m);
    setCalYear(y);
  };

  const renderCalendar = () => {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startDay = (firstDay.getDay() + 6) % 7;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cells = [];
    for (let i = 0; i < startDay; i++) {
      cells.push(<div className="cal-day empty" key={`e${i}`} />);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dt = new Date(calYear, calMonth, d);
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isPast = dt < today;
      const weekday = (dt.getDay() + 6) % 7;
      const isOff = isDayOff(weekday);
      const disabled = isPast || isOff;
      const selected = date === dateStr;
      const isToday = dt.getTime() === today.getTime();
      const dayName = DAYS_SHORT[weekday];

      let cls = 'cal-day';
      if (disabled) cls += ' off';
      if (isToday) cls += ' today';
      if (selected) cls += ' picked';

      cells.push(
        <div className={cls} key={d} onClick={disabled ? undefined : () => pickDate(dateStr)}>
          {d}
          <span className="dn">{dayName}</span>
        </div>
      );
    }
    return cells;
  };

  const getDateDisplay = () => {
    if (!date) return '';
    const dt = new Date(date);
    const dayName = DAYS_SHORT[(dt.getDay() + 6) % 7];
    return `${dayName}, ${dt.getDate()} ${MONTHS_TR[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  const getConfirmData = () => {
    if (!date || selectedServices.length === 0) return null;
    return [
      { label: 'Hizmetler', value: selectedServices.map(s => s.name).join(', ') },
      { label: 'Toplam Süre', value: `${totalDuration} dakika` },
      { label: 'Toplam Fiyat', value: `${Math.round(totalPrice)}₺`, highlight: true },
      { label: 'Tarih', value: getDateDisplay() },
      { label: 'Saat', value: time },
      { label: 'İsim', value: name },
      { label: 'Telefon', value: phone }
    ];
  };

  return (
    <div className="modal-overlay active" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-top">
          <div className="modal-top-left">
            <h3 className="modal-title">RANDEVU AL</h3>
          </div>
          <button className="modal-x" onClick={onClose}>&times;</button>
        </div>

        <div className="progress">
          {[1, 2, 3, 4, 5].map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
              <div className={`p-dot${s === step ? ' active' : ''}${s < step ? ' done' : ''}`}>{s}</div>
              {i < 4 && <div className={`p-line${s < step ? ' done' : ''}`} />}
            </div>
          ))}
        </div>

        <div className="modal-body">
          <div className="steps-wrap" style={{ transform: `translateX(-${(step - 1) * 100}%)` }}>

            {/* Step 1: Services (multi-select) */}
            <div className="step-panel">
              <p className="step-title">Hizmet Seçiniz</p>
              <p className="step-hint">Birden fazla hizmet seçebilirsiniz</p>
              <div className="svc-grid">
                {services.map(s => (
                  <div
                    key={s.id}
                    className={`svc-card${selectedServices.find(ss => ss.id === s.id) ? ' selected' : ''}`}
                    onClick={() => toggleService(s)}
                  >
                    <div className="svc-check">{selectedServices.find(ss => ss.id === s.id) ? '✓' : ''}</div>
                    <div className="ico">{s.icon}</div>
                    <div className="nm">{s.name}</div>
                    <div className="dur">{s.duration} dk</div>
                    <div className="pr">{s.price_display}</div>
                  </div>
                ))}
              </div>
              {selectedServices.length > 0 && (
                <div className="svc-summary">
                  <div className="svc-summary-info">
                    <span>{selectedServices.length} hizmet seçildi</span>
                    <span className="svc-summary-price">{Math.round(totalPrice)}₺ / {totalDuration} dk</span>
                  </div>
                  <button className="btn-next" onClick={goToCalendar}>
                    Devam Et <span>&rarr;</span>
                  </button>
                </div>
              )}
            </div>

            {/* Step 2: Calendar */}
            <div className="step-panel">
              <button className="step-back" onClick={() => goTo(1)}>&larr; Geri</button>
              <p className="step-title">Tarih Seçiniz</p>
              <div className="calendar">
                <div className="cal-head">
                  <button className="cal-arrow" onClick={() => changeMonth(-1)}>&lsaquo;</button>
                  <h4>{MONTHS_TR[calMonth]} {calYear}</h4>
                  <button className="cal-arrow" onClick={() => changeMonth(1)}>&rsaquo;</button>
                </div>
                <div className="cal-weekdays">
                  {DAYS_SHORT.map(d => <span key={d}>{d}</span>)}
                </div>
                <div className="cal-grid">{renderCalendar()}</div>
              </div>
            </div>

            {/* Step 3: Time */}
            <div className="step-panel">
              <button className="step-back" onClick={() => goTo(2)}>&larr; Geri</button>
              <p className="step-title">Saat Seçiniz</p>
              {loading ? (
                <p style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>Yükleniyor...</p>
              ) : (
                <div className="time-grid">
                  {slots.map(s => (
                    <div
                      key={s.time}
                      className={`t-slot${time === s.time ? ' picked' : ''}${s.taken ? ' taken' : ''}`}
                      onClick={s.taken ? undefined : () => pickTime(s.time)}
                    >
                      {s.time}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 4: Form + Summary */}
            <div className="step-panel">
              <button className="step-back" onClick={() => goTo(3)}>&larr; Geri</button>
              <p className="step-title">Bilgileriniz</p>

              {/* Booking Summary */}
              <div className="booking-summary">
                <div className="booking-summary-row">
                  <span className="bsr-label">Hizmetler</span>
                  <span className="bsr-value">{selectedServices.map(s => s.name).join(', ')}</span>
                </div>
                <div className="booking-summary-row">
                  <span className="bsr-label">Tarih</span>
                  <span className="bsr-value">{getDateDisplay()}</span>
                </div>
                <div className="booking-summary-row">
                  <span className="bsr-label">Saat</span>
                  <span className="bsr-value">{time}</span>
                </div>
                <div className="booking-summary-row total">
                  <span className="bsr-label">Toplam</span>
                  <span className="bsr-value bsr-price">{Math.round(totalPrice)}₺ <small>/ {totalDuration} dk</small></span>
                </div>
              </div>

              <div className="book-form">
                <div className="fg">
                  <label>Adınız Soyadınız</label>
                  <input id="custName" type="text" placeholder="Ahmet Yılmaz" value={name} onChange={e => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }} autoComplete="name" className={errors.name ? 'input-error' : ''} />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
                <div className="fg">
                  <label>Telefon Numaranız</label>
                  <input id="custPhone" type="tel" placeholder="05XX XXX XX XX" value={phone} onChange={e => { setPhone(e.target.value); setErrors(prev => ({ ...prev, phone: '' })); }} maxLength={11} autoComplete="tel" className={errors.phone ? 'input-error' : ''} />
                  {errors.phone && <span className="field-error">{errors.phone}</span>}
                </div>
                <button className="btn-book" onClick={submit} disabled={loading}>
                  {loading ? 'Gönderiliyor...' : 'Randevuyu Onayla'}
                </button>
              </div>
            </div>

            {/* Step 5: Confirmation */}
            <div className="step-panel">
              <div className="confirm">
                <div className="confirm-icon">
                  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h3>RANDEVUNUZ ONAYLANDI</h3>
                <p>Sizi bekliyoruz</p>
                {step === 5 && (
                  <div className="confirm-details">
                    {getConfirmData()?.map(row => (
                      <div className={`confirm-row${row.highlight ? ' highlight' : ''}`} key={row.label}>
                        <span className="lbl">{row.label}</span>
                        <span className="val">{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button className="btn-done" onClick={onClose}>Kapat</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
